import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { FinishFlowAction } from '../../flows/FinishFlowAction';

interface FinalInstructions {
  specialInstructions: string;
  importantContacts: string;
  documents: string;
  finalMessage: string;
  additionalWishes: string;
}

interface FuneralPreferences {
  serviceType: 'traditional' | 'memorial' | 'celebration' | 'none' | '';
  location: 'funeral_home' | 'religious' | 'home' | 'outdoor' | 'other' | '';
  customLocation: string;
  burialType: 'burial' | 'cremation' | 'donation' | 'other' | '';
  burialTypeDetails: string;
  religiousService: boolean;
  religiousDetails: string;
  musicPreferences: string;
  readings: string;
  specialRequests: string;
}

interface MemorialDetails {
  musicPreferences: string;
  readings: string;
  speakers: string;
  flowers: string;
  donations: string;
  reception: boolean;
  receptionDetails: string;
  dress: string;
  personalTouches: string;
}

const Frame223: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSessionPersistence();

  const isFuneralComplete = session.completedTasks.includes('funeral');

  const [funeralPreferences, setFuneralPreferences] = useState<FuneralPreferences>({
    serviceType: '',
    location: '',
    customLocation: '',
    burialType: '',
    burialTypeDetails: '',
    religiousService: false,
    religiousDetails: '',
    musicPreferences: '',
    readings: '',
    specialRequests: ''
  });

  const [memorialDetails, setMemorialDetails] = useState<MemorialDetails>({
    musicPreferences: '',
    readings: '',
    speakers: '',
    flowers: '',
    donations: '',
    reception: false,
    receptionDetails: '',
    dress: '',
    personalTouches: ''
  });

  const [finalInstructions, setFinalInstructions] = useState<FinalInstructions>({
    specialInstructions: '',
    importantContacts: '',
    documents: '',
    finalMessage: '',
    additionalWishes: ''
  });

  // Load all data on component mount - OPTIMIZED FOR CROSS-DEVICE SYNC
  useEffect(() => {
    // Priority 1: Load funeral preferences from session
    if (session?.taskProgress?.funeral?.answers) {
      const savedData = session.taskProgress.funeral.answers.funeralPreferences;
      if (savedData) {
        console.log('[Frame223] Loading funeralPreferences from session:', savedData);
        setFuneralPreferences(savedData);
      }
    } else {
      // Priority 2: Fall back to localStorage for funeral preferences
      try {
        const backupData = localStorage.getItem('funeral-preferences-backup');
        if (backupData) {
          const data = JSON.parse(backupData);
          if (data) {
            console.log('[Frame223] Loading funeralPreferences from localStorage backup:', data);
            setFuneralPreferences(data);
          }
        }
      } catch (error) {
        console.error('[Frame223] Failed to load funeralPreferences from backup:', error);
      }
    }

    // Priority 1: Load memorial details from session
    if (session?.taskProgress?.funeral?.answers) {
      const savedData = session.taskProgress.funeral.answers.memorialDetails;
      if (savedData) {
        console.log('[Frame223] Loading memorialDetails from session:', savedData);
        setMemorialDetails(savedData);
      }
    } else {
      // Priority 2: Fall back to localStorage for memorial details
      try {
        const backupData = localStorage.getItem('memorial-details-backup');
        if (backupData) {
          const data = JSON.parse(backupData);
          if (data) {
            console.log('[Frame223] Loading memorialDetails from localStorage backup:', data);
            setMemorialDetails(data);
          }
        }
      } catch (error) {
        console.error('[Frame223] Failed to load memorialDetails from backup:', error);
      }
    }

    // Priority 1: Load final instructions from session
    if (session?.taskProgress?.funeral?.answers) {
      const savedData = session.taskProgress.funeral.answers.finalInstructions;
      if (savedData) {
        console.log('[Frame223] Loading finalInstructions from session:', savedData);
        setFinalInstructions(savedData);
      }
    } else {
      // Priority 2: Fall back to localStorage for final instructions
      try {
        const backupData = localStorage.getItem('final-instructions-backup');
        if (backupData) {
          const data = JSON.parse(backupData);
          if (data) {
            console.log('[Frame223] Loading finalInstructions from localStorage backup:', data);
            setFinalInstructions(data);
          }
        }
      } catch (error) {
        console.error('[Frame223] Failed to load finalInstructions from backup:', error);
      }
    }
  }, [session?.taskProgress?.funeral]);

  // Helper functions for formatting display values
  const getServiceTypeLabel = (type: string) => {
    const labels = {
      traditional: 'Traditional funeral service',
      memorial: 'Memorial service (without body present)',
      celebration: 'Celebration of life',
      none: 'No formal service'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getLocationLabel = (location: string) => {
    const labels = {
      funeral_home: 'Funeral home',
      religious: 'Religious institution',
      home: 'Family home',
      outdoor: 'Outdoor location',
      other: 'Other location'
    };
    return labels[location as keyof typeof labels] || location;
  };

  const getBurialTypeLabel = (type: string) => {
    const labels = {
      burial: 'Traditional burial',
      cremation: 'Cremation',
      donation: 'Body donation to science',
      other: 'Other/Alternative'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const handleBack = () => {
    navigate('/frame-222');
  };

  const handleEdit = (section: string) => {
    switch (section) {
      case 'preferences':
        navigate('/frame-221');
        break;
      case 'memorial':
        navigate('/frame-222');
        break;
      case 'instructions':
        navigate('/frame-224');
        break;
      default:
        break;
    }
  };

  // Only show Final Instructions section if there is any content (prevents empty box due to default keys)
  const hasFinalInstructionsContent = useMemo(() => {
    return !!finalInstructions && (
      !!finalInstructions.specialInstructions ||
      !!finalInstructions.importantContacts ||
      !!finalInstructions.documents ||
      !!finalInstructions.finalMessage ||
      !!finalInstructions.additionalWishes
    );
  }, [finalInstructions]);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title={`Your Funeral & Memorial Plan Summary`}
        />

        <div className="max-w-5xl">
          <div className="text-stone-800 text-lg font-medium font-sans mb-8">
            Review your funeral and memorial plan. You can edit any section by clicking the edit button.
          </div>

          <div className="space-y-8">
            {/* Service Preferences Section */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-stone-800">Service Preferences</h3>
                <button
                  onClick={() => handleEdit('preferences')}
                  className="px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800 transition-colors text-sm font-medium"
                >
                  Edit
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">Service Type</dt>
                  <dd className="text-base text-stone-800">
                    {funeralPreferences.serviceType ? getServiceTypeLabel(funeralPreferences.serviceType) : 'Not specified'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">Location</dt>
                  <dd className="text-base text-stone-800">
                    {funeralPreferences.location ? getLocationLabel(funeralPreferences.location) : 'Not specified'}
                    {funeralPreferences.customLocation && ` - ${funeralPreferences.customLocation}`}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">Remains</dt>
                  <dd className="text-base text-stone-800">
                    {funeralPreferences.burialType ? getBurialTypeLabel(funeralPreferences.burialType) : 'Not specified'}
                  </dd>
                </div>

                {funeralPreferences.burialType === 'other' && funeralPreferences.burialTypeDetails && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">Remains Details</dt>
                    <dd className="text-base text-stone-800">{funeralPreferences.burialTypeDetails}</dd>
                  </div>
                )}

                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">Religious Service</dt>
                  <dd className="text-base text-stone-800">{funeralPreferences.religiousService ? 'Yes' : 'No'}</dd>
                </div>

                {funeralPreferences.religiousDetails && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-stone-600 mb-1">Religious Details</dt>
                    <dd className="text-base text-stone-800">{funeralPreferences.religiousDetails}</dd>
                  </div>
                )}
              </div>
            </div>

            {/* Memorial Details Section */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-stone-800">Memorial & Celebration Details</h3>
                <button
                  onClick={() => handleEdit('memorial')}
                  className="px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800 transition-colors text-sm font-medium"
                >
                  Edit
                </button>
              </div>

              <div className="space-y-4">
                {memorialDetails.musicPreferences && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">Music Preferences</dt>
                    <dd className="text-base text-stone-800">{memorialDetails.musicPreferences}</dd>
                  </div>
                )}

                {memorialDetails.readings && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">Readings or Poems</dt>
                    <dd className="text-base text-stone-800">{memorialDetails.readings}</dd>
                  </div>
                )}

                {memorialDetails.speakers && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">Speakers</dt>
                    <dd className="text-base text-stone-800">{memorialDetails.speakers}</dd>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {memorialDetails.flowers && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Flower Preferences</dt>
                      <dd className="text-base text-stone-800">{memorialDetails.flowers}</dd>
                    </div>
                  )}

                  {memorialDetails.donations && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Charitable Donations</dt>
                      <dd className="text-base text-stone-800">{memorialDetails.donations}</dd>
                    </div>
                  )}

                  {memorialDetails.dress && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Dress Code</dt>
                      <dd className="text-base text-stone-800">{memorialDetails.dress}</dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">Reception</dt>
                    <dd className="text-base text-stone-800">{memorialDetails.reception ? 'Yes' : 'No'}</dd>
                  </div>
                </div>

                {memorialDetails.receptionDetails && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">Reception Details</dt>
                    <dd className="text-base text-stone-800">{memorialDetails.receptionDetails}</dd>
                  </div>
                )}

                {memorialDetails.personalTouches && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">Personal Touches</dt>
                    <dd className="text-base text-stone-800">{memorialDetails.personalTouches}</dd>
                  </div>
                )}
              </div>
            </div>

            {/* Final Instructions Section */}
            {hasFinalInstructionsContent && (
              <div className="bg-white border border-neutral-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-stone-800">Final Instructions & Messages</h3>
                  <button
                    onClick={() => handleEdit('instructions')}
                    className="px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800 transition-colors text-sm font-medium"
                  >
                    Edit
                  </button>
                </div>

                <div className="space-y-4">
                  {finalInstructions.specialInstructions && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Special Instructions</dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">{finalInstructions.specialInstructions}</dd>
                    </div>
                  )}

                  {finalInstructions.importantContacts && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Important Contacts</dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">{finalInstructions.importantContacts}</dd>
                    </div>
                  )}

                  {finalInstructions.documents && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Documents</dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">{finalInstructions.documents}</dd>
                    </div>
                  )}

                  {finalInstructions.finalMessage && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Final Message</dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">{finalInstructions.finalMessage}</dd>
                    </div>
                  )}

                  {finalInstructions.additionalWishes && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">Additional Wishes</dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">{finalInstructions.additionalWishes}</dd>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Completion message ONLY if flow is actually completed */}
            {isFuneralComplete && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6">
                <div className="text-cyan-900 text-base font-medium font-sans mb-3">
                  Your funeral and memorial plan is complete!
                </div>
                <div className="text-cyan-800 text-sm font-normal font-sans">
                  This thoughtful planning will provide comfort and guidance to your loved ones during a difficult time.
                  You can return to edit any section at any time from the dashboard.
                </div>
              </div>
            )}
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title={`Plan Your Funeral and Memorial`}
        subtitle="Review and finalize your plan"
        progressPercent={100}
        onBack={handleBack}
        backText="Back"
        nextNode={
          <FinishFlowAction
            flowId="funeral"
            onFinished={() => navigate('/frame-140')}
            buttonText="Continue to Dashboard"
            className="!bg-cyan-700 hover:!bg-cyan-800 rounded-sm"
          />
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame223;
